import AwesomeDebouncePromise from 'awesome-debounce-promise';
import every from 'lodash/every';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import { v4 as uuid } from 'uuid';
import compact from 'lodash/compact';

import {
    DiagnosisData,
    DrugData,
    DrugStatus,
    DrugStatusChangingForm,
    EntryTypeFieldName,
    MedicalCaseCreateParams,
    MedicalCaseUpdateParams,
    OutwardReferralCreationParams,
    OutwardReferralData,
    OutwardReferralExecutionChangingParams,
    OutwardReferralUpdatingParams,
    RecommendationCreationParams,
    RecommendationData,
    RecommendationUpdateParams,
    ReferralCreateData,
    ReferralStatus,
    TaskStatus,
} from 'chaika-frontend-types/frontend-cms';
import { DictionaryNames, EntryTypeFieldType, MedicalCaseStatus } from 'chaika-enums';

import { DrugApi } from '@api/DrugApi';
import { MedicalCaseApi } from '@api/MedicalCaseApi';

import { store } from '@store';
import { DiagnosisUpdateParams } from '@store/models/SystemFields/MedicalCase/MedicalCase';
import { ReferralList } from '@store/models/SystemFields/Referral';
import {
    IMedicalCaseService,
    MedicalCaseRecommendationIdentity,
    MedicalCaseService as CommonMedicalCaseService,
} from '@store/services/MedicalCaseService';
import { dictionaryService } from '@template/services/dictionary/dictionaryService';
import { taskStore } from '@store/Task';

import { DateHelper } from '@utils/DateHelper';
import { getUniqIds } from '@utils/getUniqIds';
import { isUuid } from '@utils/isUUID';

import { DrugParams } from '@components/Blocks';
import { MedicalCaseList as MedicalCaseStore, MedicalCaseList } from '@store/models/SystemFields/MedicalCase';
import { referralService } from './referralService';
import { taskService } from '@store/Task/services';
import { loadWithChunkAndFlatten } from '@utils/loadWithChunkAndFlatten';
import { ReferralApi } from '@api';
import { serviceService } from '@services/services/serviceService';
import { userService } from '@user/services/userService';
import { ReferralField } from '@store/models/EntryFields';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { getUniqNewIds } from '@utils/getUniqNewIds';
import { assertIsDefined } from '@utils/assertIsDefined';
import { staticEntryTypeService } from '@patient/services/entry/staticEntryTypeService';
import { translate } from '@chaika/i18n/helper';
import castArray from 'lodash/castArray';

class EntryMedicalCaseService extends CommonMedicalCaseService implements IMedicalCaseService {
    private updateDiagnosisApi = AwesomeDebouncePromise(MedicalCaseApi.updateDiagnosis, 500);
    private updateDrugApi = AwesomeDebouncePromise(DrugApi.update, 500);

    public async load(): Promise<void> {
        const entry = store.entry;
        const { ehrId } = entry;

        entry.medicalCases.reset();

        const endOfEntryCreationDay = DateHelper.getEndOfDay(entry.creationTime).toISOString();
        const actualOn = entry.isSigned ? entry.updateTime : undefined; // (new Date()).toISOString();

        const [ehrMedicalCases, ehrMedicalCasesLastVersions, medicalCaseVersions, drugs] = await Promise.all([
            MedicalCaseApi.getMedicalCaseHistory({ ehrId: entry.ehrId, to: endOfEntryCreationDay }),
            MedicalCaseApi.find({ ehrId: entry.ehrId }),
            MedicalCaseApi.getMedicalCaseHistoryForEntry(entry.id),
            DrugApi.getDrugHistory({ ehrId, actualOn: actualOn ?? new Date().toISOString() }),
        ]);

        const entryMedicalCases = MedicalCaseList.getMainVersions(medicalCaseVersions).filter(
            ({ addedEntryIds, parentEntryId }) => parentEntryId === entry.id || addedEntryIds.includes(entry.id),
        );

        const entryMedicalCaseIds = new Set(entryMedicalCases.map(({ id }) => id));

        entry.drugList.set(drugs);
        entry.drugHistoryList.set(drugs);
        entry.ehrMedicalCases.setWithVersions(ehrMedicalCases);
        entry.medicalCases.setWithVersions(medicalCaseVersions.filter(({ id }) => entryMedicalCaseIds.has(id)));
        entry.ehrMedicalCasesAllVersions.set(ehrMedicalCasesLastVersions);

        const medicalCaseIds = Array.from(entryMedicalCaseIds.values());

        await Promise.all([
            taskService.loadTaskByMedicalCaseIds(medicalCaseIds),
            this.loadDrugData(drugs),
            this.loadMedicalCaseData([...ehrMedicalCases, ...medicalCaseVersions]),
            this.loadRecommendations(
                medicalCaseIds,
                entry.isSigned ? new Date(entry.signedAt ?? entry.updateTime) : new Date(),
            ),
            this.loadOutwardReferrals(medicalCaseIds),
            this.loadAllReferralData(),
        ]);
    }

    public async loadWithVersions(): Promise<void> {
        await this.load();
    }

    public async closeMedicalCase(
        medicalCaseId: string,
        medicalCasesList?: MedicalCaseStore | MedicalCaseStore[],
    ): Promise<void> {
        await this.updateStatus(medicalCaseId, MedicalCaseStatus.Closed, medicalCasesList);
    }

    public closeMedicalCasesOnEntryPublish(medicalCasesList?: MedicalCaseStore | MedicalCaseStore[]): void {
        const { authUser, entry } = store;

        castArray(medicalCasesList ?? entry.medicalCases).forEach(medicalCases => {
            medicalCases
                .getFilteredValuesIds({
                    closeDataEntryId: entry.id,
                    closeDataDoctorId: authUser.id,
                })
                .forEach(id => {
                    this.updateMedicalCaseStatusInList(id, MedicalCaseStatus.Closed, medicalCases);
                });
        });
    }

    public removeReferralsFromMedicalCase(medicalCaseId: string, referralIds: string[]): void {
        const { entry, ehr } = store;
        const { medicalCases } = entry;
        const referralList = (entry.getEntryField(EntryTypeFieldType.Referrals) as ReferralField).getValue();

        medicalCases.get(medicalCaseId)?.removeReferrals(referralIds);

        referralIds.forEach(referralId => {
            ehr.medicalCases.removeReferral(medicalCaseId, referralId);

            const referral = referralList.getByItemId(referralId);

            if (referral) {
                referralList.update(referral.groupId, {
                    ...referral,
                    medicalCaseIds: referral.medicalCaseIds.filter(id => id !== medicalCaseId),
                });
            }
        });
    }

    public removeOutwardReferralsFromMedicalCase(medicalCaseId: string, outwardReferralIds: string[]): void {
        const { medicalCases } = store.entry;

        outwardReferralIds.forEach(outwardReferralId => {
            const outwardReferral = medicalCases.getOutwardReferral(outwardReferralId);

            if (outwardReferral) {
                medicalCases.updateOutwardReferral(outwardReferralId, {
                    medicalCaseIds: outwardReferral.medicalCaseIds.filter(id => id !== medicalCaseId),
                });
            }
        });

        medicalCases.get(medicalCaseId)?.removeOutwardReferrals(outwardReferralIds);
    }

    public removeDrugsFromMedicalCase(medicalCaseId: string, drugIds: string[]): void {
        const { medicalCases } = store.entry;

        const medicalCase = medicalCases.getExisting(medicalCaseId);

        medicalCase.removeDrugIds(drugIds);
    }

    public async updateStatus(
        id: string,
        status: MedicalCaseStatus,
        medicalCasesList?: MedicalCaseStore | MedicalCaseStore[],
    ): Promise<void> {
        const { authUser, entry } = store;

        const medicalCases = medicalCasesList ? medicalCasesList : store.entry.medicalCases;

        if (status === MedicalCaseStatus.Closed) {
            castArray(medicalCases).forEach(medicalCases => {
                this.updateMedicalCaseStatusInList(id, status, medicalCases);
                this.updateMedicalCaseCloseData(medicalCases, id, {
                    entryId: entry.id,
                    closeData: {
                        entryId: entry.id,
                        doctorId: authUser.id,
                    },
                });
            });
        } else {
            await this.updateMedicalCaseStatus(medicalCases, id, status);
        }
    }

    public async create(params: MedicalCaseCreateParams): Promise<void> {
        const entry = store.entry;

        const medicalCase = await this.createMedicalCase(entry.medicalCases, { ...params, parentEntryId: entry.id });
        store.ehr.medicalCases.add([medicalCase]);
        await this.addRepliedReferralsToMedicalCase(params.id);
    }

    public async update(id: string, params: MedicalCaseUpdateParams): Promise<void> {
        const entry = store.entry;

        if (!entry.medicalCases.has(id)) {
            const medCase = entry.ehrMedicalCasesAllVersions.get(id) ?? entry.ehrMedicalCases.getExisting(id);
            entry.medicalCases.add([medCase.dto]);
        }

        await this.updateMedicalCase(entry.medicalCases, id, params, entry.id);
    }

    public async getEpisodeOptimalName(): Promise<string[]> {
        try {
            const { ehr, entry } = store;
            const objectiveSections = staticEntryTypeService.getObjectiveSections(ehr.sex);

            const fieldsData = objectiveSections
                .flatMap(section => section.blocks.flatMap(block => block.fields))
                .filter(field => field.type === EntryTypeFieldType.Text)
                .map(field => {
                    const fieldValue = entry.getField(field.name).getPreview();

                    return !!fieldValue ? `${field.title}: ${fieldValue}` : undefined;
                });

            const optimalData = await MedicalCaseApi.getEpisodeOptimalName({ textFieldsValues: compact(fieldsData) });

            if (optimalData.error) {
                throw new Error(optimalData.error);
            }

            return optimalData.episodeLabels;
        } catch (error: any) {
            store.app.showPopup({
                title: translate('errorEpisodeNamesCreatingHints'),
                description: error?.message,
            });

            return [];
        }
    }

    public async updateShareLink(id: string, welcomeMessage: string): Promise<void> {
        await this.updateMedicalCaseShareLink(store.entry.medicalCases, id, welcomeMessage);
    }

    public async attachToEntry(medicalCaseId: string): Promise<void> {
        const entry = store.entry;

        if (!entry.medicalCases.has(medicalCaseId)) {
            const medCase =
                entry.ehrMedicalCasesAllVersions.get(medicalCaseId) ?? entry.ehrMedicalCases.getExisting(medicalCaseId);
            entry.medicalCases.add([medCase.dto]);
        }

        const { relatedEntriesIds } = entry.medicalCases.getExisting(medicalCaseId);

        await MedicalCaseApi.addEntry(medicalCaseId, entry.id);

        await Promise.all([
            this.addRepliedReferralsToMedicalCase(medicalCaseId),
            this.loadOutwardReferrals([medicalCaseId]),
            taskService.loadTaskByMedicalCaseIds([medicalCaseId]),
            this.loadRecommendations(
                [medicalCaseId],
                entry.isSigned ? new Date(entry.signedAt ?? entry.updateTime) : new Date(),
            ),
            this.loadOutwardReferrals([medicalCaseId]),
            this.loadAllReferralData(),
        ]);

        entry.medicalCases.update(medicalCaseId, { addedEntryIds: [...relatedEntriesIds, entry.id] });
        entry.ehrMedicalCasesAllVersions.update(medicalCaseId, { addedEntryIds: [...relatedEntriesIds, entry.id] });
        entry.ehrMedicalCases.remove(medicalCaseId);
    }

    public async detach(medicalCaseId: string): Promise<void> {
        const entry = store.entry;
        const medCase = entry.medicalCases.getExisting(medicalCaseId);

        await MedicalCaseApi.removeEntry(medicalCaseId, entry.id);
        entry.medicalCases.remove(medicalCaseId);
        entry.ehrMedicalCases.add([medCase.dto]);
    }

    public async remove(id: string): Promise<void> {
        await MedicalCaseApi.remove(id);
        this.removeMedicalCaseFromStores(id);
    }

    public removeMedicalCaseFromStores(id: string): void {
        const { entry, ehr } = store;

        entry.medicalCases.remove(id);
        entry.ehrMedicalCases.remove(id);
        entry.ehrMedicalCasesAllVersions.remove(id);
        ehr.medicalCases.remove(id);
    }

    public async addDiagnosis(medicalCaseId: string, diagnosis: DiagnosisData): Promise<void> {
        const authUser = store.authUser;
        const { medicalCases, id: entryId } = store.entry;
        const { medicalCases: ehrMedicalCases } = store.ehr;

        const createdAt = new Date().toISOString();

        await MedicalCaseApi.addDiagnosis(medicalCaseId, { entryId, diagnosis });

        medicalCases.addDiagnosis(medicalCaseId, {
            ...diagnosis,
            createdAt,
            authorId: diagnosis.authorId ?? authUser.id,
        });
        ehrMedicalCases.addDiagnosis(medicalCaseId, {
            ...diagnosis,
            createdAt,
            authorId: diagnosis.authorId ?? authUser.id,
        });
    }

    public async updateDiagnosis(
        medicalCaseId: string,
        { icdId, clinicalDiagnosis }: DiagnosisUpdateParams,
    ): Promise<void> {
        const { medicalCases, id: entryId } = store.entry;
        const { medicalCases: ehrMedicalCases } = store.ehr;

        const diagnosis = medicalCases.getDiagnoses(medicalCaseId, icdId);
        if (!!diagnosis && diagnosis.clinicalDiagnosis !== clinicalDiagnosis) {
            await this.updateDiagnosisApi(medicalCaseId, icdId, { entryId, diagnosis: { clinicalDiagnosis } });

            medicalCases.updateDiagnosis(medicalCaseId, { icdId, clinicalDiagnosis });
            ehrMedicalCases.updateDiagnosis(medicalCaseId, { icdId, clinicalDiagnosis });
        }
    }

    public async updateDiagnosisIcd(medicalCaseId: string, oldIcdId: string, newIcdId: string): Promise<void> {
        const { medicalCases, id: entryId } = store.entry;
        const {
            ehr: { medicalCases: ehrMedicalCases },
            dictionary,
        } = store;
        const icdDictionary = dictionary.getDictionary(DictionaryNames.Mkb10);
        const defaultTitle = icdDictionary.get(newIcdId)?.title;

        const currentDiagnosis = medicalCases.getDiagnoses(medicalCaseId, oldIcdId);
        assertIsDefined(currentDiagnosis, `Diagnosis ${oldIcdId} is changed from the medical case ${medicalCaseId}`);
        const { clinicalDiagnosis: currentDescription = '', ...other } = currentDiagnosis;
        const currentDescriptionTrimmed = currentDescription.trim();

        const clinicalDiagnosis = defaultTitle
            ? currentDescriptionTrimmed
                ? `${currentDescriptionTrimmed}\n${defaultTitle}`
                : defaultTitle
            : currentDescription;

        await this.updateDiagnosisApi(medicalCaseId, oldIcdId, {
            entryId,
            diagnosis: { clinicalDiagnosis, newIcdId },
        });
        const updateParams = { ...other, oldIcd: oldIcdId, clinicalDiagnosis, icdId: newIcdId };

        medicalCases.updateDiagnosisIcd(medicalCaseId, updateParams);
        ehrMedicalCases.updateDiagnosisIcd(medicalCaseId, updateParams);
    }

    public async deleteDiagnosis(medicalCaseId: string, icdId: string): Promise<void> {
        const { medicalCases, id: entryId } = store.entry;
        const { medicalCases: ehrMedicalCases } = store.ehr;

        await MedicalCaseApi.deleteDiagnosis(medicalCaseId, icdId, entryId);
        medicalCases.removeDiagnosis(medicalCaseId, icdId);
        ehrMedicalCases.removeDiagnosis(medicalCaseId, icdId);
    }

    public async createRecommendation(
        medicalCaseId: string | string[],
        recommendation: RecommendationCreationParams,
    ): Promise<RecommendationData> {
        return this.createMedicalCaseRecommendation(store.entry.medicalCases, medicalCaseId, {
            parentEntryId: store.entry.id,
            ...recommendation,
        });
    }

    public async updateRecommendation(
        identity: MedicalCaseRecommendationIdentity,
        params: RecommendationUpdateParams,
    ): Promise<void> {
        await this.updateMedicalCaseRecommendation(store.entry.medicalCases, identity, {
            ...params,
            entryId: store.entry.id,
        });
    }

    public async updateRecommendationFromMedicalCases(
        recommendationId: string,
        params: RecommendationUpdateParams,
    ): Promise<void> {
        await this.updateMedicalCasesRecommendation(recommendationId, store.entry.medicalCases, {
            ...params,
            entryId: store.entry.id,
        });
    }

    public async changeRecommendationImportance(
        identity: MedicalCaseRecommendationIdentity,
        isImportant: boolean,
    ): Promise<void> {
        await this.updateMedicalCaseRecommendationImportance(
            store.entry.medicalCases,
            identity,
            isImportant,
            store.entry.id,
        );
    }

    public async removeRecommendation(identity: MedicalCaseRecommendationIdentity): Promise<void> {
        await this.removeMedicalCaseRecommendation(store.entry.medicalCases, identity);
    }

    public async removeRecommendationFromMedicalCases(recommendationId: string): Promise<void> {
        await this.removeRecommendationMedicalCases(recommendationId, store.entry.medicalCases);
    }

    public async loadOutwardReferrals(medicalCaseIds: string[]): Promise<void> {
        await this.loadMedicalCaseOutwardReferral(store.entry.medicalCases, medicalCaseIds);
    }

    public async loadOutwardReferralForMedicalCases(medicalCaseIds: string[]): Promise<void> {
        await this.loadOutwardReferrals(medicalCaseIds);
    }

    public async createOutwardReferral(
        medicalCaseId: string | string[],
        params: OutwardReferralCreationParams,
    ): Promise<OutwardReferralData> {
        return this.createMedicalCaseOutwardReferral(
            store.entry.medicalCases,
            medicalCaseId,
            // @ts-expect-error
            { parentEntryId: store.entry.id, ...params },
        );
    }

    public async updateOutwardReferral(
        outwardReferralId: string,
        params: OutwardReferralUpdatingParams,
    ): Promise<void> {
        await this.updateMedicalCaseOutwardReferral(store.entry.medicalCases, outwardReferralId, params);
    }

    public async updateOutwardReferralStatus(
        outwardReferralId: string,
        params: OutwardReferralExecutionChangingParams,
    ): Promise<void> {
        const executionEntryId = params.isExecuted ? store.entry.id : undefined;

        await this.updateMedicalCaseOutwardReferralStatus(store.entry.medicalCases, outwardReferralId, {
            ...params,
            executionEntryId,
        });
    }

    public async removeOutwardReferral(outwardReferralId: string): Promise<void> {
        await this.removeMedicalCaseOutwardReferral(store.entry.medicalCases, outwardReferralId);
    }

    public async createReferral(ids: string[], referral: ReferralCreateData): Promise<void> {
        const entry = store.entry;
        const withDefaultOrganization = await this.setOrganizationIdToCreateReferralParams(referral);
        const referrals = ids.map(id => ({
            ...withDefaultOrganization,
            id,
        }));

        await referralService.create(referrals);

        this.addReferrals(entry.medicalCases, referrals);
    }

    public async createReferrals(referrals: ReferralCreateData[]): Promise<void> {
        const entry = store.entry;
        const createParams = await Promise.all(
            referrals.map(referral => this.setOrganizationIdToCreateReferralParams(referral)),
        );

        await referralService.create(createParams, true);

        this.addReferrals(entry.medicalCases, createParams);
    }

    public async attachReferral(
        medicalCaseId: string | string[],
        referralGroupId: string,
        referringDoctorId: string,
    ): Promise<void> {
        const entry = store.entry;

        const referralList = entry.fields.getValue<ReferralList>(EntryTypeFieldName.Referrals);
        const referral = referralList.getExisting(referralGroupId);

        await referralService.attachReferral(medicalCaseId, referralGroupId, referringDoctorId);

        this.addReferral(store.entry.medicalCases, {
            ...referral,
            medicalCaseIds: isArray(medicalCaseId) ? medicalCaseId : [medicalCaseId],
        });
    }

    public async dettachReferral(
        medicalCaseId: string | string[],
        referralGroupId: string,
        referringDoctorId: string,
    ): Promise<void> {
        const entry = store.entry;

        const referralList = entry.fields.getValue<ReferralList>(EntryTypeFieldName.Referrals);
        const referral = referralList.getExisting(referralGroupId);

        await referralService.dettachReferral(medicalCaseId, referralGroupId, referringDoctorId);

        this.removeReferral(store.entry.medicalCases, {
            ...referral,
            medicalCaseIds: isArray(medicalCaseId) ? medicalCaseId : [medicalCaseId],
        });
    }

    public async createDrug(newIds: string[], medicalCaseId: string, createParams: DrugParams): Promise<void> {
        const { medicalCases, id: entryId, drugList, drugHistoryList } = store.entry;

        const ids = newIds.map(_ => uuid());
        const drugsParams = ids.map(id => ({
            ...createParams,
            id,
            entryId,
            medicalCaseIds: [medicalCaseId],
        }));

        // Forward-compat: новый бэк возвращает созданный drug в теле POST — берём его и обходим
        // eventual-consistency-окно между write-side и read-projection. Старый бэк отдаёт пусто —
        // тогда fallback на getList (с прежним риском гонки, до релиза бэка).
        const created = await Promise.all(drugsParams.map(param => DrugApi.create(param)));
        const drugsResult: DrugData[] = created.every(Boolean)
            ? (created as DrugData[])
            : await DrugApi.getList({ id: ids.join(',') });

        const medicalCase = medicalCases.getExisting(medicalCaseId);

        drugList.add(drugsResult);
        drugHistoryList.add(
            drugsResult.map(drug => ({
                ...drug,
                ...createParams,
                metaData: [{ entryId }],
            })),
        );
        medicalCase.addDrugIds(ids);
    }

    public async createDrugNew(newIds: string[], createParams: DrugParams): Promise<void> {
        const { medicalCases, id: entryId, drugList, drugHistoryList } = store.entry;

        const ids = newIds.map(_ => uuid());
        const drugsParams = ids.map(id => ({
            ...createParams,
            id,
            entryId,
            medicalCaseIds: createParams.medicalCaseIds ?? [],
        }));

        // Forward-compat: см. комментарий в createDrug выше.
        const created = await Promise.all(drugsParams.map(param => DrugApi.create(param)));
        const drugsResult: DrugData[] = created.every(Boolean)
            ? (created as DrugData[])
            : await DrugApi.getList({ id: ids.join(',') });

        drugList.add(drugsResult);
        drugHistoryList.add(
            drugsResult.map(drug => ({
                ...drug,
                ...createParams,
                metaData: [{ entryId }],
            })),
        );

        const drugMedicalCases = createParams.medicalCaseIds?.map(it => medicalCases.getExisting(it));

        drugMedicalCases?.forEach(medicalCase => {
            medicalCase.addDrugIds(ids);
        });
    }

    public async removeDrug(medicalCaseId: string, drugId: string): Promise<void> {
        const { drugList, id: entryId } = store.entry;
        await Promise.all([
            MedicalCaseApi.removeDrug(medicalCaseId, { drugId, entryId }),
            DrugApi.remove(drugId, { entryId }),
        ]);
        drugList.update(drugId, { status: DrugStatus.Cancelled });
        drugList.remove(drugId);
    }

    public async removeNewDrug(drugId: string, options?: Omit<DrugStatusChangingForm, 'entryId'>): Promise<void> {
        const { drugList, id: entryId, medicalCases } = store.entry;
        const drug = drugList.getExisting(drugId);
        await Promise.all([
            drug.medicalCaseIds.map(id => MedicalCaseApi.removeDrug(id, { drugId, entryId })),
            DrugApi.remove(drugId, { ...options, entryId }),
        ]);

        drug.medicalCaseIds.forEach(id => {
            medicalCases.get(id)?.removeDrugIds([drugId]);
            store.ehr.medicalCases.get(id)?.removeDrugIds([drugId]);
        });

        drugList.update(drugId, { status: DrugStatus.Cancelled });
        drugList.remove(drugId);
    }

    public async updateDrug(drugId: string, drug: DrugParams): Promise<void> {
        const { drugList, id: entryId } = store.entry;
        await this.updateDrugApi(drugId, { entryId, ...drug });

        drugList.update(drugId, drug);
    }

    public canRemoveMedicalCase(medicalCaseId: string): boolean {
        const { medicalCases } = store.entry;
        const { referralIds, outwardReferralIds, hasDrugs, recommendationIds } =
            medicalCases.getExisting(medicalCaseId);

        const hasTasks = taskStore.taskList.getFilteredValues({ medicalCaseId }).length > 0;
        const hasReferrals = referralService.getReferralsByIds(referralIds).length > 0;
        const hasOutwardReferrals = outwardReferralIds.length > 0;
        const hasRecommendations = recommendationIds.length > 0;

        return every([hasReferrals, hasOutwardReferrals, hasDrugs, hasTasks, hasRecommendations], it => !it);
    }

    public isReferralsRequiredForMedicalCase(medicalCaseId: string): boolean {
        const { medicalCases } = store.entry;
        const medicalCase = medicalCases.getExisting(medicalCaseId);

        return medicalCase.dto.isReferralsRequired;
    }

    public everyMedicalCaseValidForSign(): boolean {
        const { medicalCases } = store.entry;

        return every(
            medicalCases.values.map(
                ({ dto: { isReferralsRequired }, id, isClosed }) =>
                    isClosed ||
                    ((!isReferralsRequired || this.hasNotCanceledReferrals(id) || this.hasOutwardReferrals(id)) &&
                        this.hasNotCanceledTasks(id)),
            ),
            it => it,
        );
    }

    public everyMedicalCaseReferralsCheck(): boolean {
        const { medicalCases } = store.entry;

        return medicalCases.values.every(
            ({ dto: { isReferralsRequired }, id, isClosed }) =>
                isClosed || !isReferralsRequired || this.hasNotCanceledReferrals(id) || this.hasOutwardReferrals(id),
        );
    }

    public everyMedicalCaseTasksCheck(): boolean {
        const { medicalCases } = store.entry;

        return medicalCases.values.every(it => it.isClosed || this.hasNotCanceledTasks(it.id));
    }

    public makeAllReferralsOptional(): void {
        const { medicalCases } = store.entry;

        medicalCases.forEach(
            medicalCase =>
                medicalCase.dto.isReferralsRequired &&
                !this.hasNotCanceledReferrals(medicalCase.id) &&
                medicalCase.update({ isReferralsRequired: false }),
        );
    }

    public async loadAllReferralData(): Promise<void> {
        const entry = store.entry;

        const refEntryField = entry.getEntryField(EntryTypeFieldType.Referrals) as ReferralField;
        const notLoadedIds = getUniqNewIds(refEntryField.getValue().ids, entry.medicalCases.referralIds);

        const referrals = await loadWithChunkAndFlatten(notLoadedIds, ReferralApi.getList, 'id');

        const serviceIds = new Set<string>();
        const userIds = new Set<string>();

        referrals.forEach(it => {
            serviceIds.add(it.serviceId);
            userIds.add(it.authorId);
            if (it.userId) {
                userIds.add(it.userId);
            }
        });

        await Promise.all([
            serviceService.loadByIds(toArrayFromIterable(serviceIds)),
            userService.loadIfNotExist(toArrayFromIterable(userIds)),
        ]);

        refEntryField.getValue().addIfNotExist(referrals);
    }

    public validateTreatmentPlans(): void {
        const { medicalCases } = store.entry;

        medicalCases.forEach(medicalCase => {
            if (
                !medicalCase.isClosed &&
                !this.hasNotCanceledReferrals(medicalCase.id) &&
                this.isReferralsRequiredForMedicalCase(medicalCase.id)
            ) {
                medicalCase.setIsTreatmentPlanValid(false);
            }
        });
    }

    private async loadRecommendations(medicalCaseIds: string[], actualOn?: Date): Promise<void> {
        await this.loadMedicalCaseRecommendations(store.entry.medicalCases, medicalCaseIds, actualOn);
    }

    private hasNotCanceledReferrals(medicalCaseId: string): boolean {
        const { medicalCases } = store.entry;
        const { referralIds } = medicalCases.getExisting(medicalCaseId);
        return (
            referralService.getReferralsByIds(referralIds).filter(({ status }) => status !== ReferralStatus.Cancelled)
                .length > 0
        );
    }

    private hasOutwardReferrals(id: string): boolean {
        const { medicalCases } = store.entry;
        const medicalCase = medicalCases.getExisting(id);

        return medicalCase.hasOutwardReferrals;
    }

    private hasNotCanceledTasks(id: string): boolean {
        // getFilteredValues фильтрует без мутации общего filterParams (в отличие от setFilterParams + filteredValues),
        // поэтому проверку безопасно вызывать в том числе из рендера.
        return taskStore.taskList
            .getFilteredValues({ medicalCaseId: id })
            .some(task => task.status !== TaskStatus.Cancelled);
    }

    private async loadDrugData(drugs: DrugData[]): Promise<void> {
        const dictionaryItemIds = getUniqIds([
            ...drugs.flatMap(drug => drug.tradeNameIds ?? []),
            ...map(drugs, 'activeSubstance'),
        ]).filter(isUuid);

        await dictionaryService.loadDictionaryItemsByIds(dictionaryItemIds);
    }

    private async addRepliedReferralsToMedicalCase(medicalCaseId: string): Promise<void> {
        const entry = store.entry;

        const referralList = entry.getFieldValue<ReferralList>(EntryTypeFieldName.Referrals);
        const referrals = referralList.getFilteredValues
            ? referralList.getFilteredValues({ statuses: [ReferralStatus.Executed] })
            : [];

        const referralsWithNoCases = referrals.filter(({ id: referralId }) => {
            const cases = entry.medicalCases.getFilteredValues({ referralId });
            return cases.length === 0;
        });

        const referralPromises = referralsWithNoCases.map(({ id: referralId }) => {
            return MedicalCaseApi.addReferral(medicalCaseId, { referralId, entryId: entry.id });
        });

        await Promise.all(referralPromises);
    }
}

export const entryMedicalCaseService = new EntryMedicalCaseService();
